// Pure planning rules for the jet lag planner and the clock change planner.
// Nothing here touches the browser, so both widgets can call it from render or
// from event handlers. Every number names its source; the tool pages cite them.

export const DAY = 1440

// ---------------------------------------------------------------------------
// Sources
// ---------------------------------------------------------------------------

// CDC Yellow Book 2026, "Jet Lag Disorder" (Riedy and Williams): "an average
// rate of adaptation of 1.5 hrs/day for westward travel and 1 hr/day for
// eastward travel".
export const WEST_RATE_H = 1.5
export const EAST_RATE_H = 1

// CDC Yellow Book: jet lag disorder follows travel "across at least two time zones".
export const JET_LAG_MIN_H = 2

// CDC Yellow Book: "shifting sleep 1 hour later (for westward travel) or earlier
// (for eastward travel) per day in the 2–3 days prior to the trip".
export const PREP_STEP_H = 1
export const PREP_DAY_OPTIONS = [0, 1, 2, 3]

// Roach and Sargent (2019, Frontiers in Physiology 10:927): someone who sleeps
// 23:00 to 07:00 has "CBTmin at ~04:00", the low point of the body clock, about
// 3 hours before waking. "Light exposure in the ~12 h prior to CBTmin" delays
// the clock and "light exposure in the ~12 h after CBTmin" advances it.
export const CBT_BEFORE_WAKE_MIN = 180

// How much of that light the plan asks for. The effect is strongest close to
// the low point, so the plan concentrates on the 3 hours beside it rather than
// asking for 12 hours of daylight. This is our simplification, said on the page.
export const SEEK_WINDOW_MIN = 180
// For eastward shifts the plan asks people to keep away from bright light from
// waking until the low point (Roach and Sargent's 8 hour example: "avoid
// sunlight before midday"); 8 hours covers any morning that is before it.
export const EAST_AVOID_WINDOW_MIN = 480
// For westward shifts, light just after the low point pushes the wrong way.
export const WEST_AVOID_WINDOW_MIN = 240

// CDC Yellow Book: caffeine "Stop 6 hours before bedtime"; "Short daytime naps
// (20–30 minutes) can be utilized to help sustain alertness during the local day."
export const CAFFEINE_CUTOFF_MIN = 360

// AASM (2019, "Daylight Saving Time Advice"): starting "two to three nights before
// the time change", "Shift your bedtime 15 or 20 minutes earlier each night".
// AASM Sleep Education ("Daylight Saving Time"): 15 to 20 minutes later each night
// before the fall change, except "If you are experiencing chronic insufficient
// sleep, you should avoid shifting your bedtime later before the fall time change".
export const CLOCK_STEP_OPTIONS = [15, 20]
export const CLOCK_NIGHT_OPTIONS = [2, 3, 4]
export const CLOCK_CHANGE_MIN = 60

// AASM health advisory (2016): effects of the change "can last approximately
// five to seven days".
export const CLOCK_EFFECT_DAYS = '5 to 7'

// Smallest light window worth showing, so the plan never says "9:58 to 10:00".
const MIN_WINDOW_MIN = 30

// ---------------------------------------------------------------------------
// Time helpers
// ---------------------------------------------------------------------------

export const wrap = (minutes: number): number => ((Math.round(minutes) % DAY) + DAY) % DAY

export const parseTime = (value: string): number | null => {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value)
    if (!match) return null

    const hours = Number(match[1])
    const minutes = Number(match[2])
    if (hours > 23 || minutes > 59) return null

    return hours * 60 + minutes
}

export const formatClock = (total: number): string => {
    const wrapped = wrap(total)
    const hours24 = Math.floor(wrapped / 60)
    const minutes = wrapped % 60
    const suffix = hours24 < 12 ? 'am' : 'pm'
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12

    return `${hours12}:${String(minutes).padStart(2, '0')} ${suffix}`
}

export const formatHours = (hours: number): string => {
    const rounded = Math.round(hours * 2) / 2
    if (rounded === 1) return '1 hour'

    return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)} hours`
}

export interface TimeWindow {
    start: number
    end: number
}

// Clips a window (clock minutes, may run past midnight) to the waking day that
// starts at `wake` and ends at `bed`. Returns the longest overlap, or null.
const clipToWaking = (start: number, length: number, wake: number, bed: number): TimeWindow | null => {
    const dayEnd = bed > wake ? bed : bed + DAY
    let best: TimeWindow | null = null

    for (const turn of [-DAY, 0, DAY]) {
        const from = Math.max(start + turn, wake)
        const to = Math.min(start + turn + length, dayEnd)
        if (to - from >= MIN_WINDOW_MIN && (!best || to - from > best.end - best.start)) {
            best = { start: from, end: to }
        }
    }

    return best
}

// Moves a clock time onto the waking day that starts at `wake`, so a 1:00 am
// low point after a 7:00 am wake reads as 25:00 and compares correctly.
const onWakingDay = (time: number, wake: number): number => wake + wrap(time - wake)

// ---------------------------------------------------------------------------
// Time zones
// ---------------------------------------------------------------------------

export interface City {
    id: string
    name: string
    zone: string
    region: string
}

export const CITIES: City[] = [
    { id: 'honolulu', name: 'Honolulu', zone: 'Pacific/Honolulu', region: 'North America' },
    { id: 'anchorage', name: 'Anchorage', zone: 'America/Anchorage', region: 'North America' },
    { id: 'los-angeles', name: 'Los Angeles, San Francisco, Vancouver', zone: 'America/Los_Angeles', region: 'North America' },
    { id: 'phoenix', name: 'Phoenix', zone: 'America/Phoenix', region: 'North America' },
    { id: 'denver', name: 'Denver, Calgary', zone: 'America/Denver', region: 'North America' },
    { id: 'chicago', name: 'Chicago, Dallas, Houston', zone: 'America/Chicago', region: 'North America' },
    { id: 'mexico-city', name: 'Mexico City', zone: 'America/Mexico_City', region: 'North America' },
    { id: 'new-york', name: 'New York, Toronto, Miami', zone: 'America/New_York', region: 'North America' },
    { id: 'halifax', name: 'Halifax', zone: 'America/Halifax', region: 'North America' },
    { id: 'bogota', name: 'Bogotá', zone: 'America/Bogota', region: 'South America' },
    { id: 'lima', name: 'Lima', zone: 'America/Lima', region: 'South America' },
    { id: 'santiago', name: 'Santiago', zone: 'America/Santiago', region: 'South America' },
    { id: 'buenos-aires', name: 'Buenos Aires', zone: 'America/Argentina/Buenos_Aires', region: 'South America' },
    { id: 'sao-paulo', name: 'São Paulo, Rio de Janeiro', zone: 'America/Sao_Paulo', region: 'South America' },
    { id: 'reykjavik', name: 'Reykjavík', zone: 'Atlantic/Reykjavik', region: 'Europe' },
    { id: 'lisbon', name: 'Lisbon', zone: 'Europe/Lisbon', region: 'Europe' },
    { id: 'london', name: 'London, Dublin', zone: 'Europe/London', region: 'Europe' },
    { id: 'paris', name: 'Paris, Berlin, Rome, Madrid', zone: 'Europe/Paris', region: 'Europe' },
    { id: 'pristina', name: 'Pristina, Tirana, Belgrade', zone: 'Europe/Belgrade', region: 'Europe' },
    { id: 'athens', name: 'Athens, Helsinki', zone: 'Europe/Athens', region: 'Europe' },
    { id: 'istanbul', name: 'Istanbul', zone: 'Europe/Istanbul', region: 'Europe' },
    { id: 'moscow', name: 'Moscow', zone: 'Europe/Moscow', region: 'Europe' },
    { id: 'lagos', name: 'Lagos', zone: 'Africa/Lagos', region: 'Africa and the Middle East' },
    { id: 'cairo', name: 'Cairo', zone: 'Africa/Cairo', region: 'Africa and the Middle East' },
    { id: 'johannesburg', name: 'Johannesburg, Cape Town', zone: 'Africa/Johannesburg', region: 'Africa and the Middle East' },
    { id: 'nairobi', name: 'Nairobi', zone: 'Africa/Nairobi', region: 'Africa and the Middle East' },
    { id: 'riyadh', name: 'Riyadh, Doha', zone: 'Asia/Riyadh', region: 'Africa and the Middle East' },
    { id: 'dubai', name: 'Dubai, Abu Dhabi', zone: 'Asia/Dubai', region: 'Africa and the Middle East' },
    { id: 'karachi', name: 'Karachi', zone: 'Asia/Karachi', region: 'Asia' },
    { id: 'delhi', name: 'Delhi, Mumbai', zone: 'Asia/Kolkata', region: 'Asia' },
    { id: 'dhaka', name: 'Dhaka', zone: 'Asia/Dhaka', region: 'Asia' },
    { id: 'bangkok', name: 'Bangkok, Jakarta', zone: 'Asia/Bangkok', region: 'Asia' },
    { id: 'singapore', name: 'Singapore, Kuala Lumpur', zone: 'Asia/Singapore', region: 'Asia' },
    { id: 'hong-kong', name: 'Hong Kong', zone: 'Asia/Hong_Kong', region: 'Asia' },
    { id: 'shanghai', name: 'Beijing, Shanghai', zone: 'Asia/Shanghai', region: 'Asia' },
    { id: 'manila', name: 'Manila', zone: 'Asia/Manila', region: 'Asia' },
    { id: 'seoul', name: 'Seoul', zone: 'Asia/Seoul', region: 'Asia' },
    { id: 'tokyo', name: 'Tokyo, Osaka', zone: 'Asia/Tokyo', region: 'Asia' },
    { id: 'perth', name: 'Perth', zone: 'Australia/Perth', region: 'Oceania' },
    { id: 'brisbane', name: 'Brisbane', zone: 'Australia/Brisbane', region: 'Oceania' },
    { id: 'sydney', name: 'Sydney, Melbourne', zone: 'Australia/Sydney', region: 'Oceania' },
    { id: 'auckland', name: 'Auckland', zone: 'Pacific/Auckland', region: 'Oceania' }
]

export const CITY_REGIONS = Array.from(new Set(CITIES.map((city) => city.region)))

// UTC offset of a time zone on a given date, in minutes. Uses the date so
// daylight saving time on either end is counted.
export const zoneOffsetMinutes = (zone: string, date: Date): number => {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longOffset' }).formatToParts(date)
    const name = parts.find((part) => part.type === 'timeZoneName')?.value ?? 'GMT'
    const match = /GMT([+-])(\d{1,2}):?(\d{2})?/.exec(name)
    if (!match) return 0

    const sign = match[1] === '-' ? -1 : 1
    return sign * (Number(match[2]) * 60 + Number(match[3] ?? 0))
}

// ---------------------------------------------------------------------------
// Jet lag planner
// ---------------------------------------------------------------------------

export type Direction = 'advance' | 'delay'

export interface JetLagInput {
    // Destination clock minus home clock, in hours. Positive means the
    // destination is ahead (east).
    differenceH: number
    bed: number
    wake: number
    prepDays: number
}

export interface JetLagDay {
    key: string
    label: string
    phase: 'prep' | 'arrival'
    bed: number
    wake: number
    seek: TimeWindow | null
    seekFallback: string | null
    avoid: TimeWindow | null
    caffeineCutoff: number
    gapH: number
    notes: string[]
}

export interface JetLagPlan {
    differenceH: number
    // Which way the body clock is moved. It can differ from the map: a very
    // large eastward change is quicker to absorb by going later.
    direction: Direction
    shiftH: number
    prepShiftH: number
    arrivalDays: number
    minor: boolean
    days: JetLagDay[]
}

// Normalises any clock difference to the range -12 to under +12 hours.
const normaliseDifference = (hours: number): number => {
    const wrapped = ((hours + 12) % 24 + 24) % 24 - 12
    return Math.round(wrapped * 4) / 4
}

// Light advice for one day, given the waking schedule in local clock time and
// how far the body clock still lags behind that schedule.
const lightFor = (direction: Direction, wake: number, bed: number, gapH: number) => {
    const usualLow = wake - CBT_BEFORE_WAKE_MIN
    // Advancing: the body is still on a later clock, so its low point falls later
    // in local time. Delaying: the body is on an earlier clock.
    const low = direction === 'advance' ? usualLow + gapH * 60 : usualLow - gapH * 60

    if (direction === 'advance') {
        const seek = clipToWaking(low, SEEK_WINDOW_MIN, wake, bed)
        // Minutes from waking to the low point, read as -12 to +12 hours, so a
        // low point before waking (already slept through) comes out negative.
        const lowAfterWake = onWakingDay(low + 720, wake) - wake - 720
        const avoidLength = Math.min(lowAfterWake, EAST_AVOID_WINDOW_MIN)
        const avoid = lowAfterWake > MIN_WINDOW_MIN
            ? clipToWaking(wake + lowAfterWake - avoidLength, avoidLength, wake, bed)
            : null

        return {
            seek: seek ?? { start: wake, end: wake + 60 },
            seekFallback: seek ? null : 'Get outside soon after waking.',
            avoid
        }
    }

    const seek = clipToWaking(low - SEEK_WINDOW_MIN, SEEK_WINDOW_MIN, wake, bed)
    const avoid = clipToWaking(low, WEST_AVOID_WINDOW_MIN, wake, bed)

    return {
        seek: seek ?? { start: bed - 120, end: bed },
        seekFallback: seek ? null : 'Keep the lights bright in the evening, up to bedtime.',
        avoid
    }
}

const dayNotes = (direction: Direction, phase: 'prep' | 'arrival', index: number): string[] => {
    const notes: string[] = []

    if (phase === 'prep') {
        notes.push(
            direction === 'advance'
                ? 'Keep lights low in the last hour or two before bed.'
                : 'Keep the first hour after waking dim, with curtains closed.'
        )
        return notes
    }

    if (index === 1) {
        notes.push('Sleep on local time from the first night. If you need a nap, keep it to 20 to 30 minutes.')
    }
    if (direction === 'advance') {
        notes.push('Keep lights low in the last hour or two before bed.')
    }

    return notes
}

export const buildJetLagPlan = (input: JetLagInput): JetLagPlan => {
    const differenceH = normaliseDifference(input.differenceH)
    const magnitude = Math.abs(differenceH)

    // Pick whichever way round the clock is quicker at the CDC's average rates.
    const advanceH = differenceH >= 0 ? magnitude : 24 - magnitude
    const delayH = differenceH >= 0 ? 24 - magnitude : magnitude
    const direction: Direction = advanceH / EAST_RATE_H <= delayH / WEST_RATE_H ? 'advance' : 'delay'
    const shiftH = direction === 'advance' ? advanceH : delayH
    const rate = direction === 'advance' ? EAST_RATE_H : WEST_RATE_H

    const minor = magnitude < JET_LAG_MIN_H
    const prepDays = minor ? 0 : Math.min(input.prepDays, Math.ceil(shiftH / PREP_STEP_H))
    const prepShiftH = Math.min(prepDays * PREP_STEP_H, shiftH)
    const remainingH = shiftH - prepShiftH
    const arrivalDays = minor ? 0 : Math.ceil(remainingH / rate - 1e-9)
    const sign = direction === 'advance' ? -1 : 1

    const days: JetLagDay[] = []

    for (let step = 1; step <= prepDays; step++) {
        const movedH = Math.min(step * PREP_STEP_H, shiftH)
        const bed = input.bed + sign * movedH * 60
        const wake = input.wake + sign * movedH * 60
        // The body is assumed to be one step behind the schedule it is moving to.
        const gapH = Math.min(PREP_STEP_H, movedH)
        const light = lightFor(direction, wrap(wake), wrap(bed), gapH)
        const daysBefore = prepDays - step + 1

        days.push({
            key: `prep-${step}`,
            label: daysBefore === 1 ? 'The day before you fly' : `${daysBefore} days before you fly`,
            phase: 'prep',
            bed: wrap(bed),
            wake: wrap(wake),
            ...light,
            caffeineCutoff: wrap(bed - CAFFEINE_CUTOFF_MIN),
            gapH,
            notes: dayNotes(direction, 'prep', step)
        })
    }

    for (let day = 1; day <= arrivalDays; day++) {
        const gapH = Math.max(0, remainingH - rate * (day - 1))
        const light = lightFor(direction, input.wake, input.bed, gapH)

        days.push({
            key: `arrival-${day}`,
            label: `Day ${day} at your destination`,
            phase: 'arrival',
            bed: wrap(input.bed),
            wake: wrap(input.wake),
            ...light,
            caffeineCutoff: wrap(input.bed - CAFFEINE_CUTOFF_MIN),
            gapH,
            notes: dayNotes(direction, 'arrival', day)
        })
    }

    return { differenceH, direction, shiftH, prepShiftH, arrivalDays, minor, days }
}

// ---------------------------------------------------------------------------
// Clock change planner
// ---------------------------------------------------------------------------

export type ClockChange = 'spring' | 'fall'
export type ClockRegion = 'us' | 'eu' | 'other'

export const CLOCK_REGIONS: { value: ClockRegion; label: string }[] = [
    { value: 'us', label: 'US and Canada' },
    { value: 'eu', label: 'UK and Europe' },
    { value: 'other', label: 'Somewhere else' }
]

const utcDate = (year: number, month: number, day: number): Date => new Date(Date.UTC(year, month, day))

const nthSunday = (year: number, month: number, n: number): Date => {
    const first = utcDate(year, month, 1)
    const offset = (7 - first.getUTCDay()) % 7
    return utcDate(year, month, 1 + offset + (n - 1) * 7)
}

const lastSunday = (year: number, month: number): Date => {
    const last = utcDate(year, month + 1, 0)
    return utcDate(year, month, last.getUTCDate() - last.getUTCDay())
}

// US and Canada: second Sunday in March, first Sunday in November.
// UK and EU: last Sunday in March, last Sunday in October.
const changeDateFor = (region: ClockRegion, change: ClockChange, year: number): Date | null => {
    if (region === 'us') return change === 'spring' ? nthSunday(year, 2, 2) : nthSunday(year, 10, 1)
    if (region === 'eu') return change === 'spring' ? lastSunday(year, 2) : lastSunday(year, 9)
    return null
}

// The next upcoming date of a change, counting today. `today` is a UTC-midnight date.
export const nextChangeDate = (region: ClockRegion, change: ClockChange, today: Date): Date | null => {
    const thisYear = changeDateFor(region, change, today.getUTCFullYear())
    if (!thisYear) return null
    if (thisYear.getTime() >= today.getTime()) return thisYear

    return changeDateFor(region, change, today.getUTCFullYear() + 1)
}

// Which change comes first from today, for a sensible default.
export const soonestChange = (region: ClockRegion, today: Date): ClockChange => {
    const spring = nextChangeDate(region, 'spring', today)
    const fall = nextChangeDate(region, 'fall', today)
    if (!spring || !fall) return 'spring'

    return spring.getTime() <= fall.getTime() ? 'spring' : 'fall'
}

export const addDays = (date: Date, days: number): Date => new Date(date.getTime() + days * 86_400_000)

export const formatDay = (date: Date): string =>
    date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' })

export interface ClockChangeInput {
    change: ClockChange
    bed: number
    wake: number
    nights: number
    stepMin: number
    shortOnSleep: boolean
}

export interface ClockNight {
    key: string
    // How many nights before the change night this is (1 = the night before).
    nightsBefore: number
    bed: number
    wake: number
    shiftMin: number
}

export interface ClockChangePlan {
    change: ClockChange
    skipPrep: boolean
    nights: ClockNight[]
    preparedMin: number
}

export const buildClockChangePlan = (input: ClockChangeInput): ClockChangePlan => {
    const skipPrep = input.change === 'fall' && input.shortOnSleep
    const sign = input.change === 'spring' ? -1 : 1
    const nights: ClockNight[] = []

    if (!skipPrep) {
        for (let night = 1; night <= input.nights; night++) {
            const shiftMin = Math.min(night * input.stepMin, CLOCK_CHANGE_MIN)
            nights.push({
                key: `night-${night}`,
                nightsBefore: input.nights - night + 1,
                bed: wrap(input.bed + sign * shiftMin),
                wake: wrap(input.wake + sign * shiftMin),
                shiftMin
            })
        }
    }

    const preparedMin = nights.length ? nights[nights.length - 1].shiftMin : 0

    return { change: input.change, skipPrep, nights, preparedMin }
}
